import { forwardRef, type ForwardedRef } from 'react'
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  type PathValue,
  type UseFormSetValue,
} from 'react-hook-form'
import { FormInputNumberGroup, type FormInputNumberGroupProps } from '../form-input-number'

type FormInputNumberGroupRhfProps<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
> = Omit<FormInputNumberGroupProps, 'onChange' | 'value'> &
  Omit<ControllerProps<TValues, TName>, 'render'> & { setValue: UseFormSetValue<TValues> }

function FormInputNumberGroupRhfBase<TValues extends FieldValues, TName extends FieldPath<TValues>>(
  props: FormInputNumberGroupRhfProps<TValues, TName>,
  ref?: ForwardedRef<HTMLInputElement>,
) {
  const { control, name, rules, setValue, ...inputProps } = props
  const handleChange = (value: number) => setValue(name, value as PathValue<TValues, TName>)

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormInputNumberGroup
          {...inputProps}
          ref={ref}
          onChange={handleChange}
          value={field.value}
        />
      )}
      rules={rules}
    />
  )
}

const FormInputNumberGroupRhf = forwardRef(FormInputNumberGroupRhfBase) as <
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
>(
  props: FormInputNumberGroupRhfProps<TValues, TName> & {
    ref?: ForwardedRef<HTMLInputElement>
  },
) => ReturnType<typeof FormInputNumberGroupRhfBase>

export default FormInputNumberGroupRhf
